import { BaseService } from './base.service';
import api from './api';
import {
  ClubSalesOrder,
  ClubSalesOrderListParams,
  ClubSalesOrderListResult,
  ClubSalesOrderReportDto,
  ClubSalesOrderReportParams,
  ClubSalesOrderStats,
  CreateClubSalesOrderPayload,
  UpdateClubSalesOrderPayload,
} from '@/types/club-sales-order';

class ClubSalesOrderService extends BaseService {
  constructor() {
    super('/club-sales-orders');
  }

  async listOrders(params: ClubSalesOrderListParams): Promise<ClubSalesOrderListResult> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.locationId) query.set('locationId', params.locationId);
    if (params.search) query.set('search', params.search);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.get<ClubSalesOrderListResult>(qs ? `?${qs}` : '');
  }

  async getById(id: string): Promise<ClubSalesOrder> {
    return this.get<ClubSalesOrder>(`/${id}`);
  }

  async createOrder(payload: CreateClubSalesOrderPayload): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>('', payload);
  }

  async updateOrder(id: string, payload: UpdateClubSalesOrderPayload): Promise<ClubSalesOrder> {
    return this.put<ClubSalesOrder>(`/${id}`, payload);
  }

  async submitOrder(id: string): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>(`/${id}/submit`);
  }

  async approveOrder(id: string): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>(`/${id}/approve`);
  }

  async declineOrder(id: string, declineReason: string): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>(`/${id}/decline`, { declineReason });
  }

  async resubmitOrder(id: string): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>(`/${id}/resubmit`);
  }

  async refreshFulfillment(id: string): Promise<ClubSalesOrder> {
    return this.post<ClubSalesOrder>(`/${id}/fulfillment/refresh`);
  }

  async downloadOrderPdf(id: string, orderId?: string): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(`/club-sales-orders/${encodeURIComponent(id)}/pdf`, {
      responseType: 'blob',
    });
    const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
    const match = /filename\*?=(?:UTF-\d['']*)?"?([^";\r\n]+)"?/i.exec(disposition);
    let filename = (match?.[1]?.trim() ?? '').replace(/[\x00-\x1f\x7f/\\]/g, '').trim();
    if (!filename) filename = `Tee Box Order ${orderId ?? id}.pdf`;
    if (!/\.pdf$/i.test(filename)) filename = `${filename}.pdf`;
    return { blob: response.data as Blob, filename };
  }

  async deleteOrder(id: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/${id}`);
  }

  async getStats(): Promise<ClubSalesOrderStats> {
    return this.get<ClubSalesOrderStats>('/stats');
  }

  async getReport(params: ClubSalesOrderReportParams): Promise<ClubSalesOrderReportDto> {
    const query = new URLSearchParams();
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.locationId) query.set('locationId', params.locationId);
    const qs = query.toString();
    return this.get<ClubSalesOrderReportDto>(`/report${qs ? `?${qs}` : ''}`);
  }

  async previewApprovalRoute(id: string): Promise<{ route: string; reason?: string }> {
    return this.get<{ route: string; reason?: string }>(`/${id}/approval-preview`);
  }

  async previewApprovalRouteFromItems(
    locationId: string,
    items: { sparkHeadId?: string; brand?: string }[],
  ): Promise<{ route: string; reason?: string }> {
    return this.post<{ route: string; reason?: string }>('/approval-preview', { locationId, items });
  }
}

export const clubSalesOrderService = new ClubSalesOrderService();